import MainContent from '@/layouts/MainContent';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { Button, Checkbox, Col, Form, Row, Space, Spin } from 'antd';
import { DEFAULT_DATE_FORMAT, ROW_GUTTER } from '@/config';
import { LeftOutlined, RightOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons';
import AppDatePicker from '@/components/FormItems/DatePicker';
import StationInput from '@/components/FormItems/StationInput';
import CountryInput from '@/components/FormItems/CountryInput';
import { range, sortBy } from 'lodash';
import moment, { Moment } from 'moment';
import './styles.less';
import TripRow from './tripCalendarTable/TripRow';
import InfiniteScroll from 'react-infinite-scroll-component';
import { TransportRoleType, TripStatusType } from '@/constants';
import RouteInput from '@/components/FormItems/RouteInput';
import { TripDetailCalendarDto, BusAutocompletionModel, UserBasicInfoModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import TripInfoModal from './TripInfoModal';
const PAGE_TITLE = 'tripsCalendarPage_Title';

const hourOfDay = range(0, 24);
const PAGE_SIZE = 50;

export default function TripsCalendarPage() {
  const { t } = useTranslation();
  const [searchForm] = Form.useForm();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [pageIndex, setPageIndex] = React.useState<number>(1);
  const [hasMore, setHasMore] = React.useState<boolean>(false);
  const [trips, setTrips] = React.useState<TripDetailCalendarDto[]>([]);
  const [total, setTotal] = React.useState<number>(0);
  const [trip, setTrip] = React.useState<TripDetailCalendarDto>();
  const [transportDrivers, setTransportDrivers] = React.useState<UserBasicInfoModel[]>([]);
  const [buses, setBuses] = React.useState<BusAutocompletionModel[]>([]);
  React.useEffect(() => {
    apiSdk.CompanyUserService.getUserByRoles({ roles: [TransportRoleType.Driver, TransportRoleType.CoDriver] }).then(res => {
      setTransportDrivers(res);
    });
    apiSdk.BusService.getAutocompletionList().then(res => {
      setBuses(res);
    });
    onSearch({});
  }, []);

  const onSearch = values => {
    setLoading(true);
    setPageIndex(1);
    apiSdk.TripDetailService.getCalendarTripDetails({
      departureDate: moment(values.date || new Date()).format(DEFAULT_DATE_FORMAT),
      stationId: values.stationId,
      routeId: values.routeId,
      includePassingStation: values.includePassingStation,
      pageIndex: 1,
      pageSize: PAGE_SIZE,
    })
      .then(res => {
        setHasMore((res.total || 0) > PAGE_SIZE * (res.pageIndex || 0));
        setTrips(res.data || []);
        setTotal(res.total || 0);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onNextPrevDay = (offset: number) => {
    var currentDate: Moment = searchForm.getFieldValue('date');
    if (currentDate) {
      var nextDay = moment(currentDate).add(offset, 'day');
      if (nextDay.isAfter(moment().startOf('day'))) {
        searchForm.setFieldValue('date', nextDay);
        searchForm.submit();
      }
    }
  };
  const next = () => {
    let p = pageIndex;
    setPageIndex(p => p + 1);
    const values = searchForm.getFieldsValue();
    apiSdk.TripDetailService.getCalendarTripDetails({
      departureDate: moment(values.date).format(DEFAULT_DATE_FORMAT),
      stationId: values.stationId,
      pageIndex: p + 1,
      pageSize: PAGE_SIZE,
    }).then(res => {
      setHasMore((res.total || 0) > PAGE_SIZE * (res.pageIndex || 0));
      setTrips(t => [...t, ...(res.data || [])]);
      setTotal(res.total || 0);
    });
  };
  const filterData = Form.useWatch('date', searchForm);
  const countryId = Form.useWatch('countryId', searchForm);
  return (
    <div style={{ paddingBottom: 20, height: '100%' }} id="trips-calendar">
      <MainContent title={t(PAGE_TITLE)}>
        <Helmet title={t(PAGE_TITLE)} />

        <div className="ant-layout-content-body">
          <Form
            layout="vertical"
            className="nta-custom-form"
            form={searchForm}
            onFinish={onSearch}
            initialValues={{ date: moment(), includePassingStation: true }}
            style={{ marginTop: 10 }}
          >
            <Row gutter={ROW_GUTTER}>
              <Col md={12} xl={4} xxl={4}>
                <Form.Item label={t('tripDetailsPage_DepartureDate')} rules={[{ required: true, message: '' }]}>
                  <div style={{ display: 'flex', width: '100%' }}>
                    <Button
                      icon={<LeftOutlined />}
                      onClick={() => {
                        onNextPrevDay(-1);
                      }}
                    />
                    <div style={{ flex: 1, marginLeft: 5, marginRight: 5 }}>
                      <Form.Item name="date" rules={[{ required: true, message: '' }]}>
                        <AppDatePicker className="w-100" allowClear={false} disabledDate={v => v.isBefore(moment().startOf('day'))} />
                      </Form.Item>
                    </div>
                    <Button
                      icon={<RightOutlined />}
                      onClick={() => {
                        onNextPrevDay(1);
                      }}
                    />
                  </div>
                </Form.Item>
              </Col>
              <Col md={12} xl={4} xxl={4}>
                <Form.Item label={t('discountsPage_Route')} required={false} name="routeId">
                  <RouteInput placeholder={t('discountsPage_Route')} />
                </Form.Item>
              </Col>
              <Col md={12} xl={4} xxl={4}>
                <Form.Item label={t('tripsCalendarPage_Country')} required={false} name="countryId">
                  <CountryInput
                    placeholder={t('tripsCalendarPage_Country')}
                    onChange={() => {
                      searchForm.setFieldValue('sourceId', null);
                    }}
                  />
                </Form.Item>
              </Col>
              {countryId ? (
                <Col md={12} xl={4} xxl={4}>
                  <Form.Item required={false} label={t('tripsCalendarPage_Station')} name="stationId" rules={[{ required: true, message: '' }]}>
                    <StationInput
                      placeholder={t('tripsCalendarPage_Station')}
                      countryIds={countryId ? [countryId] : undefined}
                      disabled={!countryId}
                    />
                  </Form.Item>
                </Col>
              ) : null}

              <Col md={12} xl={4} xxl={4}>
                <Form.Item label="  " name="includePassingStation" valuePropName="checked">
                  <Checkbox>{t('tripsCalendarPage_IncludePassingStation')}</Checkbox>
                </Form.Item>
              </Col>
              <Col md={12} xl={4} xxl={4}>
                <Form.Item label="&nbsp;" style={{ textAlign: 'left' }}>
                  <Space>
                    <Button type="primary" htmlType="submit">
                      <SearchOutlined />
                      {t('zTable.search')}
                    </Button>
                    <Button type="ghost" htmlType="reset">
                      <CloseOutlined />
                      {t('zTable.reset')}
                    </Button>
                  </Space>
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <Spin spinning={loading}>
            <div className="trip-table-container">
              <InfiniteScroll
                next={next}
                hasMore={hasMore}
                dataLength={trips.length}
                loader={
                  <div style={{ width: '100%', height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin />
                  </div>
                }
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
                  <b>{`${t('zTable.records')}${total}`}</b>
                  <div style={{ flex: 1, alignItems: 'center', justifyContent: 'flex-end', display: 'flex' }}>
                    {sortBy(TripStatusType.__getKeys(), x => TripStatusType[x]).map(key => {
                      return (
                        <div
                          style={{
                            backgroundColor: TripStatusType.__getColor(TripStatusType[key]),
                            width: 80,
                            height: 30,
                            borderRadius: 5,
                            marginLeft: 5,
                            lineHeight: '30px',
                            color: 'white',
                            textAlign: 'center',
                            fontSize: 12,
                            fontWeight: 'bold',
                          }}
                        >
                          {key}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <table className="trip-table">
                  <thead>
                    <tr>
                      <th className="no">No.</th>
                      <th className="trip-name">{t('tripsCalendarPage_TripName')}</th>
                      {hourOfDay.map(h => {
                        return (
                          <th className={`hour`} key={h}>
                            {moment()
                              .set('hour', h)
                              .format('HH:[00]')}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {trips.map((trip, index) => {
                      return <TripRow key={index} index={index + 1} trip={trip} date={filterData} showDetail={t => setTrip(t)} />;
                    })}
                  </tbody>
                </table>
              </InfiniteScroll>
            </div>
          </Spin>
        </div>
        {trip ? <TripInfoModal buses={buses} transportDrivers={transportDrivers} onClose={() => setTrip(undefined)} trip={trip} /> : null}
      </MainContent>
    </div>
  );
}
